import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Slider, message } from 'antd';
import { humanFileSize } from './utils';
import ServerPricingInfo from './ServerPricingInfo';

interface NewServerProps {
	onCreated?: () => void;
}

export interface AddServerPayload {
	appname: string;
	size: number;
	location: string;
	url_name: string;
	url_domain: string;
}

interface AddServerResponse {
	ok?: boolean;
	task?: string;
	err?: string;
	error?: string;
}

interface AddServerStateResponse {
	ok?: boolean;
	state?: string;
	msg?: string;
	err?: string;
	error?: string;
}

interface FormValues {
	appname: string;
	size: number;
	size_gb: number;
	url_name: string;
	url_domain: string;
	location: string;
}

const MIN_SERVER_SIZE = 1 * 1024 * 1024 * 1024;
const MAX_SERVER_SIZE = 20 * 1024 * 1024 * 1024 * 1024;
const SERVER_SIZE_STEP = 1 * 1024 * 1024 * 1024;
const DEFAULT_SERVER_SIZE = 10 * 1024 * 1024 * 1024;
const BYTES_PER_GB = 1024 * 1024 * 1024;
const MAX_SERVER_SIZE_GB = MAX_SERVER_SIZE / BYTES_PER_GB;

const NewServer: React.FC<NewServerProps> = ({ onCreated }) => {
	const [visible, setVisible] = useState<boolean>(false);
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [statusText, setStatusText] = useState<string>('');
	const [errorText, setErrorText] = useState<string>('');
	const [form] = Form.useForm();
	const mountedRef = useRef<boolean>(true);

	useEffect(() => {
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const setSafeState = (setter: () => void) => {
		if (mountedRef.current) {
			setter();
		}
	};

	const normalizeError = (data: { err?: string; error?: string }): string => {
		if (data.err) {
			return data.err;
		}
		if (data.error) {
			return data.error;
		}
		return 'Unknown error';
	};

	const pollState = async (taskUuid: string, openUrl: string): Promise<void> => {
		while (mountedRef.current) {
			let stateData: AddServerStateResponse;
			try {
				const formData = new FormData();
				formData.append('setup_uuid', taskUuid);

				const resp = await fetch('/api/server_action_state', {
					method: 'POST',
					credentials: 'include',
					body: formData,
				});
				stateData = await resp.json();
			} catch (error) {
				setSafeState(() => setErrorText('Connection issue while checking setup state'));
				return;
			}

			if (!stateData.ok) {
				setSafeState(() => setErrorText(normalizeError(stateData)));
				return;
			}

			setSafeState(() => setStatusText(stateData.msg ? stateData.msg : 'Working...'));

			if (stateData.state === 'AVAILABLE') {
				setSafeState(() => {
					setSubmitting(false);
					setVisible(false);
					setStatusText('');
					setErrorText('');
					form.resetFields();
					message.success('Server setup completed');
					if (onCreated) {
						onCreated();
					}
					Modal.confirm({
						title: 'Server is available',
						content: 'Open the new server now?',
						onOk: () => {
							window.open(openUrl, '_blank');
						},
					});
				});
				return;
			}

			if (stateData.state === 'ERROR') {
				setSafeState(() => {
					setSubmitting(false);
					setErrorText(stateData.msg ? stateData.msg : 'Server setup failed');
				});
				return;
			}

			await new Promise((resolve) => setTimeout(resolve, 1000));
		}
	};

	const handleSubmit = async (values: any) => {
		const formValues = values as FormValues;
		setSubmitting(true);
		setStatusText('Starting server setup...');
		setErrorText('');

		const payload: AddServerPayload = {
			appname: formValues.appname,
			size: formValues.size,
			location: formValues.location,
			url_name: formValues.url_name,
			url_domain: formValues.url_domain,
		};
		const openUrl = 'https://' + formValues.url_name + '.' + formValues.url_domain;

		let data: AddServerResponse;
		try {
			const formData = new FormData();
			formData.append('data', JSON.stringify(payload));

			const resp = await fetch('/api/add_server', {
				method: 'POST',
				credentials: 'include',
				body: formData,
			});
			data = await resp.json();
		} catch (error) {
			setSubmitting(false);
			setErrorText('Connection issue with service');
			return;
		}

		if (!data.ok || !data.task) {
			setSubmitting(false);
			setErrorText(normalizeError(data));
			return;
		}

		setStatusText('Server setup started...');
		await pollState(data.task, openUrl);
	};

	const openDialog = () => {
		setVisible(true);
		setErrorText('');
		setStatusText('');
		form.setFieldsValue({
			location: 'Germany',
			url_domain: 'urbackup.app',
			size: DEFAULT_SERVER_SIZE,
			size_gb: DEFAULT_SERVER_SIZE / BYTES_PER_GB,
		});
	};

	const handleSliderChange = (value: number | [number, number]) => {
		if (Array.isArray(value)) {
			return;
		}

		form.setFieldsValue({
			size: value,
			size_gb: Math.round(value / BYTES_PER_GB),
		});
	};

	const handleGbChange = (value: number | undefined) => {
		if (typeof value === 'undefined') {
			return;
		}

		const clampedGb = Math.max(1, Math.min(MAX_SERVER_SIZE_GB, Math.round(value)));
		form.setFieldsValue({
			size_gb: clampedGb,
			size: clampedGb * BYTES_PER_GB,
		});
	};

	const closeDialog = () => {
		if (submitting) {
			return;
		}
		setVisible(false);
		setErrorText('');
		setStatusText('');
		form.resetFields();
	};

	return (
		<>
			<Button type="primary" onClick={openDialog}>
				Add server
			</Button>
			<Modal
				title="Add new server"
				visible={visible}
				onCancel={closeDialog}
				footer={null}
				destroyOnClose
			>
				<Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ location: 'Germany', url_domain: 'urbackup.app', size: DEFAULT_SERVER_SIZE, size_gb: DEFAULT_SERVER_SIZE / BYTES_PER_GB }}>
					<Form.Item
						name="appname"
						label="App name"
						rules={[
							{ required: true, message: 'Please enter an app name' },
							{ min: 1, max: 63, message: 'App name must be 1 to 63 characters' },
							{ pattern: /^[A-Za-z0-9-]+$/, message: 'Allowed characters: A-Z, a-z, 0-9 and -' },
						]}
					>
						<Input placeholder="Example: Testserver10" disabled={submitting} />
					</Form.Item>

					<Form.Item
						name="url_name"
						label="URL name"
						rules={[{ required: true, message: 'Please enter a URL name' }]}
					>
						<Input placeholder="Example: testserver49" disabled={submitting} />
					</Form.Item>

					<Form.Item
						name="size"
						label="Storage size"
						rules={[{ required: true, message: 'Please select a storage size' }]}
					>
						<Slider
							disabled={submitting}
							min={MIN_SERVER_SIZE}
							max={MAX_SERVER_SIZE}
							step={SERVER_SIZE_STEP}
							onChange={handleSliderChange}
							marks={{
								[MIN_SERVER_SIZE]: '1 GiB',
								[DEFAULT_SERVER_SIZE]: '10 GiB',
								[MAX_SERVER_SIZE]: '20 TiB',
							}}
						/>
					</Form.Item>
					<Form.Item
						name="size_gb"
						label="Storage size (GB)"
						rules={[{ required: true, message: 'Please enter a storage size in GB' }]}
					>
						<InputNumber
							disabled={submitting}
							min={1}
							max={MAX_SERVER_SIZE_GB}
							step={1}
							precision={0}
							style={{ width: '100%' }}
							onChange={handleGbChange}
						/>
					</Form.Item>
					<Form.Item noStyle shouldUpdate>
						{() => {
							const currentSize = Number(form.getFieldValue('size') || DEFAULT_SERVER_SIZE);
							return (
								<div style={{ marginTop: -14, marginBottom: 12 }}>
									Selected: {humanFileSize(currentSize, false)}
								</div>
							);
						}}
					</Form.Item>

					<Form.Item name="url_domain" label="URL domain" rules={[{ required: true, message: 'Please select a URL domain' }]}>
						<Select disabled={submitting}>
							<Select.Option value="urbackup.app">urbackup.app</Select.Option>
						</Select>
					</Form.Item>

					<Form.Item name="location" label="Location" rules={[{ required: true, message: 'Please select a location' }]}>
						<Select disabled={submitting}>
							<Select.Option value="Germany">Germany</Select.Option>
						</Select>
					</Form.Item>

					{statusText.length > 0 && <Alert style={{ marginBottom: 12 }} message={statusText} type="info" showIcon />}
					{errorText.length > 0 && <Alert style={{ marginBottom: 12 }} message={errorText} type="error" showIcon />}
					
					<Form.Item noStyle shouldUpdate>
						{() => {
							const currentSizeGb = Number(form.getFieldValue('size_gb') || DEFAULT_SERVER_SIZE / BYTES_PER_GB);
							return <ServerPricingInfo sizeGb={currentSizeGb} />;
						}}
					</Form.Item>

					<Form.Item style={{ marginBottom: 0 }}>
						<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
							<Button onClick={closeDialog} disabled={submitting}>
								Cancel
							</Button>
							<Button type="primary" htmlType="submit" loading={submitting}>
								Create for a fee
							</Button>
						</div>
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

export default NewServer;
